import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { getCached, getCachedAt, setCached } from "../utils/wsCache";

// Default 7-day range
const defaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6); // last 7 days including today
    const fmt = (d) => d.toISOString().split("T")[0];
    return { start_date: fmt(start), end_date: fmt(end) };
};

const normalizeRange = (r) => {
    if (!r) return defaultRange();
    if (r.start_date && r.end_date) return r;
    if (r.fromDate && r.toDate) return { start_date: r.fromDate, end_date: r.toDate };
    return defaultRange();
};

const SETTLEMENT_TYPE = 'cms_get_charging_sessions_with_settlement';

// set once the server answers "Unknown CMS type" for SETTLEMENT_TYPE (backend not
// deployed yet); from then on this tab uses the plain sessions request instead
let settlementEndpointMissing = false;

// Pass { withSettlement: true } to also get settlement_status / settlement_amount per session.
export default function useChargingSessionsByGroup(groupId, range = null, { withSettlement = false } = {}) {
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const { start_date: fromDate, end_date: toDate } = normalizeRange(range);
    const cacheKey = `charging_sessions_by_group${withSettlement ? ':settlement' : ''}:${groupId}:${fromDate}:${toDate}`;

    const [sessions, setSessions] = useState(() => getCached(cacheKey) || []);
    const [isLoading, setIsLoading] = useState(() => !getCached(cacheKey));
    const [lastUpdated, setLastUpdated] = useState(() => getCachedAt(cacheKey));
    const [settlementAvailable, setSettlementAvailable] = useState(withSettlement && !settlementEndpointMissing);

    // resolves with the settlement response, or rejects with { unsupported: true }
    // as soon as the server reports it doesn't know the request type
    const requestWithSettlement = useCallback((payload) => new Promise((resolve, reject) => {
        const off = registerHandler('error', (msg) => {
            if (typeof msg.message === 'string' && msg.message.includes(SETTLEMENT_TYPE)) {
                off();
                reject(Object.assign(new Error('Settlement data is not available on this server'), { unsupported: true }));
            }
        });
        send(SETTLEMENT_TYPE, payload).then(
            (r) => { off(); resolve(r); },
            (e) => { off(); reject(e); }
        );
    }), [send, registerHandler]);

    const fetchSessions = useCallback(async (attempt = 0, force = false) => {
        if (!mountedRef.current || !groupId) return;

        // already fetched in this tab: show it without asking the server again.
        // a browser refresh clears the cache; live *_update messages keep it current
        const cached = getCached(cacheKey);
        if (cached && !force) {
            setSessions(cached);
            setLastUpdated(getCachedAt(cacheKey));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');
        setMessage('');

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        // keep showing the loading state; this runs again once the socket connects
        if (!isConnected) return;

        try {

            const payload = { group_id: groupId, from_date: fromDate, to_date: toDate };
            let response;
            let responseType = 'cms_get_charging_sessions_by_group_id_response';

            if (withSettlement && !settlementEndpointMissing) {
                try {
                    response = await requestWithSettlement(payload);
                    responseType = `${SETTLEMENT_TYPE}_response`;
                } catch (err) {
                    if (!err.unsupported) throw err;
                    settlementEndpointMissing = true;
                }
            }
            if (!response) {
                if (withSettlement && mountedRef.current) setSettlementAvailable(false);
                response = await send('cms_get_charging_sessions_by_group_id', payload);
            }

            if (!response || response.type !== responseType) {
                throw new Error('Invalid response from server');
            }
            if (response.success === false) {
                throw new Error(response.message || 'Server error');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('No data available. Try refreshing the page');
            }

            setCached(cacheKey, response.data);
            setSessions(response.data);
            setLastUpdated(getCachedAt(cacheKey));

        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchSessions(attempt + 1);
                }, delay);
            }

        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, send, groupId, fromDate, toDate, cacheKey, withSettlement, requestWithSettlement]);

    useEffect(() => {
        mountedRef.current = true;
        fetchSessions();

        const unregister = registerHandler(`charging_sessions_update_group_${groupId}`, (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setCached(cacheKey, msg.data);
                setSessions(msg.data);
                setLastUpdated(getCachedAt(cacheKey));
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchSessions, registerHandler, groupId, cacheKey]);

    return { sessions, isLoading, error, message, settlementAvailable, lastUpdated, refetch: () => fetchSessions(0, true) };
}
